import {useI18n} from 'i18n/I18nContext'
import React, {useState} from 'react'
import styled, {css} from 'styled-components'
import {DefaultBlockConfigValues} from 'TemplateEditor/Layout/Blocks/DefaultBlockConfig'
import {
  DefaultBlockHolder,
  errorBackgroundPulse,
} from 'TemplateEditor/Layout/Blocks/DefaultBlockHolder'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {PreviewHolderDiv} from 'TemplateEditor/Layout/Sidebar/BlocksList'
import {Section} from 'TemplateEditor/template'
import {v4 as uuidv4} from 'uuid'
import {ButtonOneConfigType} from 'TemplateEditor/Layout/Blocks/ButtonOne/ButtonOne'
import useProductPayment from 'attendee/Billing/Payments/useProductPayment'
import {useAttendeeBilling} from 'attendee/Billing/Providers/AttendeeBillingProvider'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import {attendeeApiClient} from 'attendee/apiClient'
import {useEvent} from 'event/providers/EventProvider'
import {
  DigitalProductType,
  Product,
} from 'organization/Event/Products/EventProductsProvider'
import AttendeeInfoModal from 'TemplateEditor/Layout/Blocks/ButtonSales/AttendeeInfoModel'

export const ButtonSalesTypeId = 'block-button-sales'

type ButtonSalesProps = {
  block: ButtonSalesConfigType
  section: Section
  setEditMode: () => void
  key: string | number
  editMode: boolean
}

export function ButtonSales(props: ButtonSalesProps) {
  const {editMode, nonInteractiveSalesInViewerMode} = useEditor()

  if (editMode || nonInteractiveSalesInViewerMode) {
    return <ButtonSalesPreview {...props} />
  }

  return <ButtonSalesAttendee {...props} />
}

function ButtonSalesPreview(props: ButtonSalesProps) {
  const {block, setEditMode} = props
  // block.productId
  return (
    <DefaultBlockHolder block={block} key={props.key} onClick={setEditMode}>
      <>
        {!props.block.text && <PreviewButtonSales />}
        {props.block.text && (
          <DefaultButton hasError={!block.productId} block={block}>
            {props.block.text}
          </DefaultButton>
        )}
      </>
    </DefaultBlockHolder>
  )
}

function ButtonSalesAttendee(props: ButtonSalesProps) {
  const {block, setEditMode} = props

  const {payPayment} = useAttendeeBilling()
  const {payment} = useProductPayment(block.productId)
  const {getProduct} = useProductInfo()
  const [product, setProduct] = useState<Product | null>(null)

  const [showModal, setShowModal] = useState<boolean>(false)
  const toggleModal = () => {
    setShowModal((curr) => !curr)
  }

  const handleClick = async () => {
    if (!block.productId) return

    const product = await getProduct(block.productId)

    if (product.type === DigitalProductType) {
      payPayment(payment())
      return
    }

    if (
      product.need_phone === false &&
      product.need_name === false &&
      product.need_address === false
    ) {
      payPayment(payment())
      return
    }

    setProduct(product)
    setShowModal(true)
  }

  return (
    <DefaultBlockHolder block={block} key={props.key} onClick={setEditMode}>
      <>
        {!props.block.text && <PreviewButtonSales />}
        {props.block.text && (
          <DefaultButton block={block} onClick={handleClick}>
            {props.block.text}
          </DefaultButton>
        )}
        {product && (
          <AttendeeInfoModal
            show={showModal}
            toggle={toggleModal}
            product={product}
          />
        )}
      </>
    </DefaultBlockHolder>
  )
}

export function PreviewButtonSales() {
  const {t} = useI18n()
  return (
    <PreviewHolderDiv>
      <PointOfSaleIcon />
      <span>{t('templateEditor.blockSales')}</span>
    </PreviewHolderDiv>
  )
}

export type ButtonSalesConfigType = Omit<
  ButtonOneConfigType,
  'link' | 'linkType'
> & {
  productId: number | null
}

export function newButtonSales(): ButtonSalesConfigType {
  return {
    ...DefaultBlockConfigValues(),
    id: uuidv4(),
    type: ButtonSalesTypeId,
    backgroundColor: '#ffffff',
    textColor: '#333333',
    text: '',
    productId: null,
    textAlign: 'left',
    font: 'Comfortaa',
    width: 100,
    height: 50,
    fontSize: 1.5,
    mobile: {
      ...DefaultBlockConfigValues().mobile,
      width: 100,
      fontSize: 1.5,
      height: 50,
    },
  }
}

export function useProductInfo() {
  const apiClient = attendeeApiClient()
  const {event} = useEvent()

  const getProduct = async (productId: number) => {
    return await apiClient.get<Product>(
      `api/event/${event?.id}/products/${productId}`,
    )
  }

  return {getProduct}
}

const DefaultButton = styled.div<{
  block: ButtonSalesConfigType
  hasError?: boolean
}>`
  background: ${(props) => props.block.backgroundColor};
  color: ${(props) => props.block.textColor};
  width: ${(props) => props.block.width}%;
  height: ${(props) => props.block.height}px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${(props) => props.block.font};
  cursor: pointer;
  border-radius: ${(props) => props.block.border.radius}px;
  border-width: ${(props) => props.block.border.width}px;
  border-color: ${(props) => props.block.border.color};
  border-style: solid;
  font-size: ${(props) => props.block.fontSize}rem;
  text-align: ${(props) => props.block.textAlign};

  ${(props) =>
    props.hasError &&
    css`
      animation: ${errorBackgroundPulse} 1s infinite;
    `}

  @container (max-width: 991px) {
    width: ${(props) => props.block.mobile.width}%;
    height: ${(props) => props.block.mobile.height}px;
    font-size: ${(props) => props.block.mobile.fontSize}rem;
  }
`
